import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useLazyResetPasswordQuery } from '@greenkub-fronts/data-access';

import { usePostHog } from 'posthog-js/react';

type FormData = {
  password: string;
  passwordConfirmation: string;
};

export function useResetPasswordFormFeature() {
  const { token } = useParams();

  const navigate = useNavigate();

  const [postResetPassword, postResetPasswordQuery] =
    useLazyResetPasswordQuery();

  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const postHog = usePostHog();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const onPasswordVisibleToggle = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const onSubmit: SubmitHandler<FormData> = (data) => {
    if (postResetPasswordQuery.isLoading) return;

    if (data.password !== data.passwordConfirmation) {
      setSuccessMsg(null);
      setErrorMsg('Les mots de passe ne sont pas identiques');
      return;
    }

    setSuccessMsg(null);
    setErrorMsg(null);

    postResetPassword({ token, plainPassword: data.password }).then(
      (resp: any) => {
        if (resp.error) {
          setSuccessMsg(null);
          setErrorMsg(resp.error.message);
          postHog?.capture('reset-password-error', {
            token,
          });
        } else {
          setSuccessMsg(resp.data.message);
          setErrorMsg(null);
          postHog?.capture('reset-password', {
            token,
          });
          navigate('/login');
        }
      }
    );
  };

  return {
    register,
    handleSubmit,
    errors,
    onSubmit,
    focusedField,
    setFocusedField,
    isPasswordVisible,
    onPasswordVisibleToggle,
    successMsg,
    errorMsg,
    isLoading: postResetPasswordQuery.isLoading,
  };
}

export default useResetPasswordFormFeature;
